/**
 * Explicit storefront performance loader.
 *
 * This loader only acts on elements intentionally annotated with data-performance-*.
 * It never patches browser prototypes, replays lifecycle events, or rewrites
 * arbitrary App markup. Keep commerce, consent, analytics, and the LCP image
 * out of interaction-only scheduling.
 */
(() => {
  'use strict';

  const DEFAULTS = Object.freeze({
    debug: false,
    idleTimeoutMs: 2000,
    hardFallbackMs: 4500,
    visibleRootMargin: '300px 0px',
  });
  const config = Object.assign({}, DEFAULTS, window.themePerformanceLoaderConfig || {});
  const loadedElements = new WeakSet();
  const inFlightScripts = new Map();
  const moduleTasks = new Map();
  const observedAssets = new Set();
  const observedByTarget = new Map();
  const observedRecommendations = new WeakSet();
  const optionalModuleSelectors = Object.freeze({
    slideshow: 'slideshow-component',
    splide: 'splide-component',
    'product-enhancements': 'clickable-discount, dynamic-dates, bundle-deals, quantity-breaks, quantity-gifts, main-bundle-offer, custom-product-field',
  });
  let visibleObserver = null;
  let interactionTriggered = false;
  let idleTriggered = false;
  let fallbackTriggered = false;

  const log = (...args) => {
    if (config.debug) console.debug('[theme performance]', ...args);
  };

  const mark = (name) => {
    try {
      performance.mark(`theme-performance:${name}`);
    } catch (_) {
      // Performance marks are diagnostic only.
    }
  };

  const assetSelector = (trigger) =>
    `[data-performance-trigger~="${trigger}"][data-performance-src], ` +
    `[data-performance-trigger~="${trigger}"][data-performance-srcset]`;

  const allAssetSelector = '[data-performance-src], [data-performance-srcset]';

  const assetIsEligible = (asset, root) => {
    const requiredSelector = asset.dataset.performanceSelector;
    if (!requiredSelector) return true;

    const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    try {
      if (scope.nodeType === 1 && scope.matches(requiredSelector)) return true;
      return Boolean(scope.querySelector(requiredSelector));
    } catch (error) {
      log('invalid performance selector', requiredSelector, error);
      return false;
    }
  };

  const findAssets = (root, selector) => {
    const assets = [];
    if (root && root.nodeType === 1 && root.matches(selector)) {
      assets.push(root);
    }
    if (root && typeof root.querySelectorAll === 'function') {
      assets.push(...root.querySelectorAll(selector));
    }
    return assets.filter((asset) => assetIsEligible(asset, root));
  };

  const copyAttribute = (from, to, name) => {
    if (from.hasAttribute(name)) to.setAttribute(name, from.getAttribute(name));
  };

  const notify = (kind, element, status, src, error) => {
    element.dataset.performanceState = status;
    loadedElements.add(element);
    mark(`${kind}:${status}:${element.dataset.performanceId || src}`);
    document.dispatchEvent(
      new CustomEvent('theme:performance-asset-loaded', {
        detail: {
          kind,
          id: element.dataset.performanceId || '',
          src,
          status,
          error: error || null,
        },
      })
    );
  };

  const loadScript = (placeholder) => {
    const src = placeholder.dataset.performanceSrc;
    if (!src) return Promise.resolve();

    if (inFlightScripts.has(src)) {
      notify('script', placeholder, 'deduplicated', src);
      return inFlightScripts.get(src);
    }

    const task = new Promise((resolve) => {
      const script = document.createElement('script');
      const requestedType = placeholder.dataset.performanceScriptType;

      script.src = src;
      script.async = placeholder.dataset.performanceAsync === 'true';
      if (requestedType) script.type = requestedType;

      for (const name of ['id', 'nonce', 'integrity', 'crossorigin', 'referrerpolicy']) {
        copyAttribute(placeholder, script, name);
      }

      script.addEventListener(
        'load',
        () => {
          notify('script', placeholder, 'loaded', src);
          resolve({ status: 'loaded', src });
        },
        { once: true }
      );
      script.addEventListener(
        'error',
        (event) => {
          notify('script', placeholder, 'error', src, event);
          resolve({ status: 'error', src, error: event });
        },
        { once: true }
      );

      placeholder.replaceWith(script);
      log('loading script', src);
    });

    inFlightScripts.set(src, task);
    return task;
  };

  const loadIframe = (iframe) => {
    const src = iframe.dataset.performanceSrc;
    if (!src) return Promise.resolve();
    iframe.loading = iframe.loading || 'lazy';
    iframe.src = src;
    delete iframe.dataset.performanceSrc;
    notify('iframe', iframe, 'loaded', src);
    return Promise.resolve({ status: 'loaded', src });
  };

  const loadMediaSource = (source, mediaToReload) => {
    const src = source.dataset.performanceSrc;
    if (!src) return Promise.resolve();
    source.src = src;
    delete source.dataset.performanceSrc;
    const media = source.closest('video, audio');
    if (media && mediaToReload) mediaToReload.add(media);
    notify('media', source, 'loaded', src);
    return Promise.resolve({ status: 'loaded', src });
  };

  const loadImage = (image) => {
    const src = image.dataset.performanceSrc;
    const srcset = image.dataset.performanceSrcset;
    if (!src && !srcset) return Promise.resolve();
    if (srcset) image.srcset = srcset;
    if (src) image.src = src;
    delete image.dataset.performanceSrc;
    delete image.dataset.performanceSrcset;
    notify('image', image, 'loaded', src || srcset);
    return Promise.resolve({ status: 'loaded', src: src || srcset });
  };

  const loadElement = (element, mediaToReload) => {
    if (!element || loadedElements.has(element)) return Promise.resolve();

    switch (element.tagName) {
      case 'SCRIPT':
        return loadScript(element);
      case 'IFRAME':
        return loadIframe(element);
      case 'SOURCE':
        return loadMediaSource(element, mediaToReload);
      case 'IMG':
        return loadImage(element);
      default:
        notify('asset', element, 'unsupported', '');
        return Promise.resolve({ status: 'unsupported' });
    }
  };

  const modulePlaceholderSelector = '[data-performance-module][data-performance-src]';

  const moduleIsNeededIn = (moduleName, root = document) => {
    const selector = optionalModuleSelectors[moduleName];
    if (!selector) return false;

    const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    try {
      if (scope.nodeType === 1 && scope.matches(selector)) return true;
      return Boolean(scope.querySelector(selector));
    } catch (error) {
      log('invalid optional module selector', selector, error);
      return false;
    }
  };

  const findModulePlaceholder = (moduleName) =>
    findAssets(document, modulePlaceholderSelector).find(
      (element) => element.dataset.performanceModule === moduleName
    );

  const loadModuleElement = (element) => {
    const moduleName = element?.dataset.performanceModule;
    if (!moduleName) return loadElement(element);
    if (moduleTasks.has(moduleName)) return moduleTasks.get(moduleName);

    const task = loadElement(element).then((result) => ({
      ...(result || { status: 'loaded', src: element.dataset.performanceSrc || '' }),
      module: moduleName,
    }));
    moduleTasks.set(moduleName, task);
    return task;
  };

  const ensureModule = (moduleName, root = document) => {
    if (!optionalModuleSelectors[moduleName]) {
      return Promise.resolve({ status: 'unsupported', module: moduleName });
    }
    if (moduleTasks.has(moduleName)) return moduleTasks.get(moduleName);
    if (!moduleIsNeededIn(moduleName, root)) {
      return Promise.resolve({ status: 'not-needed', module: moduleName });
    }

    const placeholder = findModulePlaceholder(moduleName);
    if (!placeholder) {
      return Promise.resolve({ status: 'not-found', module: moduleName });
    }
    return loadModuleElement(placeholder);
  };

  const loadOrdered = async (elements, reason) => {
    const queue = [...new Set(elements)]
      .filter((element) => !loadedElements.has(element))
      .sort((a, b) => Number(a.dataset.performanceOrder || 0) - Number(b.dataset.performanceOrder || 0));
    const mediaToReload = new Set();

    if (!queue.length) return;
    mark(`trigger:${reason}`);
    log(`trigger ${reason}`, queue);

    for (const element of queue) {
      await (element.dataset.performanceModule
        ? loadModuleElement(element)
        : loadElement(element, mediaToReload));
    }

    // Source siblings must be attached before media.load() runs. Reloading once
    // avoids re-selecting and downloading multiple representations of one video.
    // A component may use preload="none" to keep a fallback source ready without
    // starting a media transfer before the component requests playback.
    for (const media of mediaToReload) {
      if (media.preload !== 'none' && typeof media.load === 'function') media.load();
    }
  };

  const getByTrigger = (trigger, root = document) => findAssets(root, assetSelector(trigger));

  const triggerInteraction = () => {
    if (interactionTriggered) return;
    interactionTriggered = true;
    loadOrdered(getByTrigger('interaction'), 'interaction');
  };

  const triggerIdle = () => {
    if (idleTriggered) return;
    idleTriggered = true;
    loadOrdered(getByTrigger('idle'), 'idle');
  };

  const observationTargetFor = (asset) => {
    if (asset.tagName === 'SOURCE') return asset.closest('video, audio') || asset;
    return asset;
  };

  const observeVisible = (asset) => {
    if (!visibleObserver || loadedElements.has(asset)) return;
    const target = observationTargetFor(asset);
    let assets = observedByTarget.get(target);
    if (!assets) {
      assets = new Set();
      observedByTarget.set(target, assets);
      visibleObserver.observe(target);
    }
    assets.add(asset);
    observedAssets.add(asset);
  };

  const stopObserving = (asset) => {
    const target = observationTargetFor(asset);
    const assets = observedByTarget.get(target);
    if (assets) {
      assets.delete(asset);
      if (!assets.size) {
        observedByTarget.delete(target);
        if (visibleObserver) visibleObserver.unobserve(target);
      }
    }
    observedAssets.delete(asset);
  };

  const setupVisibleObserver = () => {
    const visible = getByTrigger('visible');
    if (!('IntersectionObserver' in window)) {
      if (visible.length) loadOrdered(visible, 'visible-fallback');
      return;
    }

    visibleObserver = new IntersectionObserver(
      (entries) => {
        const entering = [];
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const assets = observedByTarget.get(entry.target) || new Set();
          for (const asset of [...assets]) {
            stopObserving(asset);
            entering.push(asset);
          }
        }
        loadOrdered(entering, 'visible');
      },
      { rootMargin: config.visibleRootMargin }
    );

    visible.forEach(observeVisible);
  };

  const scheduleIdle = () => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(triggerIdle, { timeout: config.idleTimeoutMs });
    } else {
      window.setTimeout(triggerIdle, Math.min(config.idleTimeoutMs, 1500));
    }
  };

  const loadHardFallbacks = (root = document) => {
    fallbackTriggered = true;
    loadOrdered(
      findAssets(
        root,
        '[data-performance-fallback="true"][data-performance-src], [data-performance-fallback="true"][data-performance-srcset]'
      ),
      'hard-fallback'
    );
  };

  const findElements = (root, selector) => {
    const elements = [];
    if (root && root.nodeType === 1 && root.matches(selector)) elements.push(root);
    if (root && typeof root.querySelectorAll === 'function') {
      elements.push(...root.querySelectorAll(selector));
    }
    return [...new Set(elements)];
  };

  const requestOptionalModulesIn = (root = document) => {
    Object.keys(optionalModuleSelectors).forEach((moduleName) => {
      if (moduleIsNeededIn(moduleName, root)) ensureModule(moduleName, root);
    });
  };

  const observeProductRecommendations = (root = document) => {
    findElements(root, 'product-recommendations').forEach((recommendations) => {
      if (observedRecommendations.has(recommendations)) return;
      observedRecommendations.add(recommendations);

      const checkForOptionalModules = () => requestOptionalModulesIn(recommendations);
      checkForOptionalModules();

      if (!('MutationObserver' in window)) return;
      const observer = new MutationObserver(checkForOptionalModules);
      observer.observe(recommendations, { childList: true, subtree: true });
    });
  };

  window.themePerformanceLoader = Object.assign({}, window.themePerformanceLoader || {}, {
    ensureModule,
  });
  document.addEventListener('theme:optional-module-needed', (event) => {
    const moduleName = event.detail && (event.detail.module || event.detail.moduleName);
    if (moduleName) ensureModule(moduleName, event.target || document);
  });

  const registerSection = (root) => {
    loadOrdered(getByTrigger('immediate', root), 'section-load');
    if (visibleObserver) getByTrigger('visible', root).forEach(observeVisible);
    if (idleTriggered) loadOrdered(getByTrigger('idle', root), 'section-idle');
    if (interactionTriggered) loadOrdered(getByTrigger('interaction', root), 'section-interaction');
    if (fallbackTriggered) loadHardFallbacks(root);
    requestOptionalModulesIn(root);
    observeProductRecommendations(root);
  };

  const start = () => {
    if (window.Shopify && Shopify.designMode) {
      loadOrdered(findAssets(document, allAssetSelector), 'shopify-design-mode');
      requestOptionalModulesIn(document);
      observeProductRecommendations(document);
      document.addEventListener('shopify:section:load', (event) => {
        loadOrdered(findAssets(event.target, allAssetSelector), 'shopify-section-load');
        requestOptionalModulesIn(event.target);
        observeProductRecommendations(event.target);
      });
      return;
    }

    loadOrdered(getByTrigger('immediate'), 'immediate');
    setupVisibleObserver();
    scheduleIdle();
    observeProductRecommendations(document);

    const interactionOptions = { once: true, capture: true, passive: true };
    window.addEventListener('pointerdown', triggerInteraction, interactionOptions);
    window.addEventListener('touchstart', triggerInteraction, interactionOptions);
    window.addEventListener('keydown', triggerInteraction, { once: true, capture: true });

    window.setTimeout(() => loadHardFallbacks(), config.hardFallbackMs);

    document.addEventListener('shopify:section:load', (event) => registerSection(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      for (const asset of [...observedAssets]) {
        if (event.target.contains(asset)) stopObserving(asset);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
